import axiosInstance from './axiosConfig';

export type AppVoiceUiStatus =
  | 'idle'
  | 'fetching_token'
  | 'connecting'
  | 'ringing'
  | 'connected'
  | 'disconnecting'
  | 'error';

export interface AppVoiceState {
  status: AppVoiceUiStatus;
  errorMessage: string | null;
}

type Listener = (state: AppVoiceState) => void;

let twilioVoiceSdkPromise: Promise<typeof import('@twilio/voice-sdk')> | null = null;

async function loadTwilioVoiceSdk() {
  if (!twilioVoiceSdkPromise) {
    twilioVoiceSdkPromise = import('@twilio/voice-sdk');
  }
  return twilioVoiceSdkPromise;
}

class AppTwilioVoiceService {
  private device: import('@twilio/voice-sdk').Device | null = null;
  private activeCall: import('@twilio/voice-sdk').Call | null = null;
  private tokenExpiresAtSec = 0;
  private readonly listeners = new Set<Listener>();
  private state: AppVoiceState = { status: 'idle', errorMessage: null };

  /** Support-call session: timeline row is logged once per ended session (see `consumeLastSupportCallSession`). */
  private supportCallSessionStartMs: number | null = null;
  private supportCallHadProgress = false;
  private supportCallFinalized = false;
  private pendingSupportCallLog: { durationSec: number; outcome: string } | null = null;

  private resetSupportCallSessionTracking() {
    this.supportCallSessionStartMs = null;
    this.supportCallHadProgress = false;
    this.supportCallFinalized = false;
    this.pendingSupportCallLog = null;
  }

  private finalizeSupportCallSession(outcome: string) {
    if (this.supportCallFinalized) return;
    this.supportCallFinalized = true;

    if (!this.supportCallHadProgress) {
      this.pendingSupportCallLog = null;
      this.supportCallSessionStartMs = null;
      return;
    }

    const start = this.supportCallSessionStartMs;
    const durationSec =
      start != null ? Math.max(0, Math.floor((Date.now() - start) / 1000)) : 0;
    this.pendingSupportCallLog = { durationSec, outcome };
    this.supportCallSessionStartMs = null;
    this.supportCallHadProgress = false;
  }

  /**
   * Returns call summary for the last ended support VoIP session, if any, then clears it.
   * Only set when the call reached ringing or connected (not instant connect failures).
   */
  consumeLastSupportCallSession(): { durationSec: number; outcome: string } | null {
    const out = this.pendingSupportCallLog;
    this.pendingSupportCallLog = null;
    return out;
  }

  getState(): AppVoiceState {
    return { ...this.state };
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  private notify(patch: Partial<AppVoiceState>) {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((fn) => fn(this.state));
  }

  async destroy(): Promise<void> {
    await this.hangUp();
    if (this.device) {
      try {
        await this.device.unregister();
      } catch {
        /* ignore */
      }
      this.device.destroy();
      this.device = null;
    }
    this.tokenExpiresAtSec = 0;
    this.notify({ status: 'idle', errorMessage: null });
  }

  private async ensureDevice(): Promise<void> {
    const { Device } = await loadTwilioVoiceSdk();
    const nowSec = Date.now() / 1000;

    if (this.device && this.tokenExpiresAtSec - 120 > nowSec) {
      if (this.device.state !== Device.State.Registered) {
        await this.device.register();
      }
      return;
    }

    if (this.device) {
      try {
        await this.device.unregister();
      } catch {
        /* ignore */
      }
      this.device.destroy();
      this.device = null;
    }

    this.notify({ status: 'fetching_token', errorMessage: null });

    const { data } = await axiosInstance.post<{
      token: string;
      identity: string;
      expiresIn: number;
    }>('/api/app-voice/token', {});

    const ttl = data.expiresIn ?? 3600;
    this.tokenExpiresAtSec = nowSec + ttl;

    this.device = new Device(data.token, {
      logLevel: 'error',
    });

    this.device.on('error', (err) => {
      console.error('[AppVoice] Device error:', err);
    });

    await this.device.register();
  }

  /**
   * Outbound VoIP into the same TwiML app + IVR + TaskRouter path as WhatsApp / DID.
   * Custom params are posted to /api/twilio/voice/app/inbound (see appVoiceController).
   */
  async startSupportCall(params: {
    threadId: string;
    clientId: string;
    clientName: string;
  }): Promise<void> {
    if (this.activeCall) {
      throw new Error('A call is already in progress');
    }

    this.resetSupportCallSessionTracking();
    this.notify({ status: 'connecting', errorMessage: null });

    try {
      await this.ensureDevice();
      if (!this.device) {
        throw new Error('Voice device unavailable');
      }

      const call = await this.device.connect({
        params: {
          thread_id: params.threadId,
          client_id: params.clientId,
          client_name: params.clientName,
        },
      });

      this.activeCall = call;

      const markProgress = () => {
        this.supportCallHadProgress = true;
        if (this.supportCallSessionStartMs == null) {
          this.supportCallSessionStartMs = Date.now();
        }
      };

      call.on('accept', () => {
        markProgress();
        this.notify({ status: 'connected' });
      });
      call.on('ringing', () => {
        markProgress();
        if (this.state.status === 'connecting') {
          this.notify({ status: 'ringing' });
        }
      });
      call.on('disconnect', () => {
        this.finalizeSupportCallSession('completed');
        this.activeCall = null;
        this.notify({ status: 'idle', errorMessage: null });
      });
      call.on('cancel', () => {
        this.finalizeSupportCallSession('cancelled');
        this.activeCall = null;
        this.notify({ status: 'idle', errorMessage: null });
      });
      call.on('reject', () => {
        this.finalizeSupportCallSession('rejected');
        this.activeCall = null;
        this.notify({ status: 'idle', errorMessage: null });
      });
      call.on('error', (err: unknown) => {
        this.finalizeSupportCallSession('failed');
        this.activeCall = null;
        const message = err instanceof Error ? err.message : 'Call error';
        this.notify({ status: 'error', errorMessage: message });
      });
    } catch (e: unknown) {
      this.activeCall = null;
      const message = e instanceof Error ? e.message : 'Could not start call';
      this.notify({ status: 'error', errorMessage: message });
      throw e;
    }
  }

  sendDigit(digit: string): void {
    if (!this.activeCall || !digit) return;
    const d = digit.slice(0, 1);
    if (!/[0-9*#wW]/.test(d)) return;
    try {
      this.activeCall.sendDigits(d);
    } catch (e) {
      console.warn('[AppVoice] sendDigits failed', e);
    }
  }

  setMuted(shouldMute: boolean): void {
    if (!this.activeCall) return;
    this.activeCall.mute(shouldMute);
  }

  isMuted(): boolean {
    return this.activeCall?.isMuted() ?? false;
  }

  async hangUp(): Promise<void> {
    if (!this.activeCall) {
      this.notify({ status: 'idle', errorMessage: null });
      return;
    }
    this.notify({ status: 'disconnecting', errorMessage: null });
    try {
      this.activeCall.disconnect();
    } catch {
      /* ignore */
    }
    this.finalizeSupportCallSession('completed');
    this.activeCall = null;
    this.notify({ status: 'idle', errorMessage: null });
  }
}

export const appTwilioVoiceService = new AppTwilioVoiceService();
